import { NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { Component, DestroyRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';

import {
    CasinoLobbyService,
    CasinoManager,
    CasinoStylesService,
    ConfigProviderService,
    HeaderVisibilityOnScrollService,
    JACKPOTS_CATEGORY,
    JackpotAndPromotionsService,
    JackpotDataService,
    LMTDataClientConfig,
    OptimizedTeaserManager,
    SmartBannerService,
    TeasersStore,
    UtilityService,
} from '@casinocore/platform/core';
import { AppearanceThemeModifierDirective } from '@frontend/casino-ui';
import { CasinoTheme, FeatureNames, UIElement } from '@frontend/casino-ui/shared';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsPill } from '@frontend/ui/pill';
import {
    CookieService,
    DeviceService,
    HeaderService,
    NavigationService,
    Page,
    ProductService,
    TrackingService,
    UserService,
} from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { rxEffects } from '@rx-angular/state/effects';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { CategoryiconSubtitleComponent } from '../../categoryicon-subtitle/categoryicon-subtitle.component';
import { SignPostingComponent } from '../../sign-posting/sign-posting.component';

@Component({
    selector: 'cc-sub-navigation-menu',
    templateUrl: 'sub-navigation-menu.component.html',
    host: { '(document:click)': 'hideSubNavigationMenu(true)', '[style.top.px]': 'subNavigationTop()' },
    imports: [
        NgIf,
        NgClass,
        NgStyle,
        SignPostingComponent,
        CategoryiconSubtitleComponent,
        NgFor,
        RouterLink,
        DsPill,
        DsButtonIcon,
        AppearanceThemeModifierDirective,
        IconCustomComponent,
    ],
})
export class SubNavigationMenuComponent implements OnInit, OnDestroy, OnChanges {
    @Input() lobbyType: string;
    @Input() categoryRoute: string;
    @Input() stickyInputQueryElement: string;

    private readonly utilityService = inject(UtilityService);
    private readonly headerVisibilityOnScrollService = inject(HeaderVisibilityOnScrollService);

    isTouch = signal(false);
    isDefaultCategory: boolean;
    arcadeLobbyGameConfig: any;
    arcadeLobbyMessages: any;
    arcadeCoinExpiry: number;
    ShowArcadeCoinExpiry: boolean = false;
    showCoinExpiryText: boolean = false;
    coinBalSubscription: Subscription;
    currentLobbyType: string;
    isMobileWidth = computed(() => {
        if (this.enableEpcotSubNavigation() || this.enablePillsSubNavigation()) {
            return this.isTouch();
        } else {
            return this.deviceService.isMobileWidth();
        }
    });
    showLastLevelCategoryDropdown: boolean = false;
    title = '';
    defaultCategory: any;
    subNavBackGroundColor: string;
    categoryType: any;
    selectedCategory: any;
    lobbyObs: any;
    categoryLookUpObs: any;
    prevLobbyCategories: any = [];
    parentCategory: any;
    categoryRelativeUrlTemplate: string;
    isFullLobbyLoaded: boolean;
    isUkHeading: boolean;
    alignIconRight: boolean;
    enableDefaultHomeUrl: boolean;
    defaultProductHomeUrl: string;
    isSignPosting: boolean;
    displayTitle: string;
    displayHeader: boolean = true;
    isEnableOptimizedTeasers: boolean;
    private readonly rxEffects = rxEffects();
    enableHeaderforCategory: boolean;
    enableFixedMobileNavigation: boolean;
    subCategoryTitleFontSize: string;
    subCategoryTextColor: string;
    lobbyTileFontFamily: string;

    normalTeasersArray: any[] = [];
    optimizedTeasersArray: any[] = [];
    subnavigationTeaserLength: number = 0;
    teaserObs: Subscription;
    navigationBackgroundColor: boolean;
    isSignPostingCount: boolean;
    navBackground: any;
    NavBackBtnIconColor: any;
    hideCategoryTitleForTournaments: boolean = false;
    hideSRTitleSubscription: Subscription;
    readonly enableEpcotSubNavigation = signal(false);
    readonly enablePillsSubNavigation = signal(false);
    readonly enableVnIconsForPills = signal(false);
    private readonly hasHeaderElement = toSignal(this.headerService.whenReady.pipe(map(() => this.headerService.hasHeaderElement())));
    private readonly headerElementHeight = computed(() => (this.hasHeaderElement() ? this.headerService.headerElementRect().height : 0));
    readonly subNavigationTop = computed(() => this.headerElementHeight() + this.smartBannerService.smartBannerRect().height);
    jackpotTileCategory: boolean;
    snapShotGroupId: any;
    jackpotGroupType: any;
    isJackpotLandscape: boolean;
    categoryname: any;
    jackpotGroupName: any;
    readonly showPill = computed(() => this.enablePillsSubNavigation() || this.enableEpcotSubNavigation());
    public buttonType: UIElement = 'buttonIcon';
    public navItemType: UIElement = 'pill';
    public featureName: FeatureNames = FeatureNames.NAVBAR;
    public currentTheme: CasinoTheme;
    showV2JackpotModule: boolean;
    isJackpotsCategory = signal(false);
    private hideHeaderOnScroll = signal(false);
    private destroyRef = inject(DestroyRef);

    private teasersStore = inject(TeasersStore);
    readonly teaserInitialisedObservable = toObservable(this.teasersStore.initialised);
    private MOBILE_BREAKPOINT = 767;

    constructor(
        private casinoManager: CasinoManager,
        private user: UserService,
        private headerService: HeaderService,
        private navigation: NavigationService,
        private smartBannerService: SmartBannerService,
        private tracking: TrackingService,
        private mcPage: Page,
        private router: Router,
        private cookie: CookieService,
        private configProviderService: ConfigProviderService,
        private casinoLobbyService: CasinoLobbyService,
        private optimizedTeaserManager: OptimizedTeaserManager,
        private deviceService: DeviceService,
        private jackpotDataService: JackpotDataService,
        private casinoStyles: CasinoStylesService,
        private jackpotAndPromotionsService: JackpotAndPromotionsService,
        private productService: ProductService,
    ) {
        effect(() => {
            if (this.isMobileWidth()) {
                this.adjustSubNavOnLandscapeMode();
            }
        });
        const initializeStickyHeaderEffect = effect(() => {
            if (this.hideHeaderOnScroll() && this.hasHeaderElement()) {
                this.headerVisibilityOnScrollService.initializeStickyBehavior();
                initializeStickyHeaderEffect.destroy();
            }
        });
    }

    ngOnInit(): void {
        this.currentTheme = this.utilityService.getCurrentTheme();
        const jackpotDataConfig = this.configProviderService.provideJackpotConfig();
        this.showV2JackpotModule = jackpotDataConfig.showV2JackpotModule;
        this.isJackpotLandscape = this.deviceService.windowWidth() >= 768 ? true : false;
        this.isJackpotsCategory.set(this.categoryRoute === JACKPOTS_CATEGORY);
        this.navBackground = this.configProviderService.provideGlobalConfig()?.messages?.NavBackBtnBackground;
        this.NavBackBtnIconColor = this.configProviderService.provideGlobalConfig()?.messages?.NavBackBtnIconColor;
        this.isTouch.set(this.casinoManager.isTouch());

        this.categoryRelativeUrlTemplate = '/' + this.mcPage.lang + '/' + this.casinoManager.getLobbyUrlPrefix(this.lobbyType) + '/c/';
        if (!this.getFromCookie('dark-mode')) {
            const global: any = this.configProviderService.provideGlobalConfig();
            if (this.navigationBackgroundColor) {
                this.subNavBackGroundColor = global.lobbyConfigurations.navigationBackgroundColor;
            } else {
                this.subNavBackGroundColor = global.lobbyConfigurations.lobbyBackgroundColor;
            }
        } else {
            this.subNavBackGroundColor = 'transparent';
        }
        this.bindSubNavigation();
        if (!this.isDefaultCategory) {
            this.teaserObs = this.teaserInitialisedObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isInitialized: boolean) => {
                if (isInitialized) {
                    this.getSubNavTeasersLength();
                }
            });
        }
        this.hideSLotRacCategoryTitle();
    }
    ngOnChanges(changes: SimpleChanges) {
        if (changes.lobbyType || changes.categoryRoute) {
            this.assignFeatures();
        }
        this.bindSubNavigation();
    }

    assignFeatures() {
        this.isJackpotsCategory.set(this.categoryRoute === JACKPOTS_CATEGORY);
        this.arcadeLobbyGameConfig = this.configProviderService.provideGameConfig()?.arcadeLobbyType.toLowerCase();
        this.arcadeLobbyMessages = this.configProviderService.provideGlobalConfig()?.arcadeLobbyConfigurations;
        this.currentLobbyType = this.casinoManager.getCurrentLobby()?.toLowerCase();
        const features: any = this.configProviderService.provideFeaturesConfig();
        const subnavConfig: any = this.configProviderService.provideSubNavigationConfig();
        this.alignIconRight = this.casinoManager.IsFeatureEnabledforLobbytype(this.lobbyType, features.alignIconRight);
        this.isSignPosting = this.casinoManager.IsFeatureEnabledforLobbytype(this.lobbyType, features.enableSignpostingSticker);
        this.isUkHeading = this.casinoManager.IsFeatureEnabledforLobbytype(this.lobbyType, features.isUkHeading);
        this.enableHeaderforCategory = features.enableHeaderforCategory;
        this.enableFixedMobileNavigation = features.enableFixedMobileNavigation;
        this.navigationBackgroundColor = features.navigationBackgroundColor;
        this.enableEpcotSubNavigation.set(subnavConfig.enableEpcotSubNavigation);
        this.enablePillsSubNavigation.set(subnavConfig.enablePillsSubNavigation);
        this.enableVnIconsForPills.set(!!subnavConfig.enableVnIconsForPills);
        const lmtData: any = this.configProviderService.provideLmtConfig();
        this.defaultProductHomeUrl = lmtData.defaultProductHomeUrl;
        this.enableDefaultHomeUrl = this.casinoManager.IsFeatureEnabledforLobbytype(this.lobbyType, lmtData.enableDefaultHomeUrl);
        const optimizedTeaserClientConfig: any = this.configProviderService.provideOptimizedTeaserConfig();
        this.isEnableOptimizedTeasers = optimizedTeaserClientConfig.teaserOptimization;
        const categoryInfo = this.casinoLobbyService.getCategoryInfo(this.lobbyType, this.categoryRoute);
        this.subCategoryTitleFontSize = this.casinoStyles.getsubCategoryTitleFontSize(categoryInfo);
        this.subCategoryTextColor = this.casinoStyles.getsubCategoryTextColor(categoryInfo);
        this.lobbyTileFontFamily = this.casinoStyles.getLTileFontFamily(categoryInfo);
        this.ShowArcadeCoinExpiry = this.arcadeLobbyGameConfig == this.currentLobbyType && this.user.isAuthenticated;
        if (this.ShowArcadeCoinExpiry) {
            this.subscribeToCoinBalance();
        }
        this.hideHeaderOnScroll.set(!!features.hideHeaderOnScroll);
    }

    bindSubNavigation() {
        const selectCatInfo: any = this.casinoLobbyService.getCategoryInfo(this.lobbyType, this.categoryRoute);
        if (selectCatInfo) {
            const selectCInfo = JSON.parse(JSON.stringify(selectCatInfo));
            if (selectCInfo?.categoryInfo) {
                this.selectedCategory = selectCInfo.categoryInfo;
                this.setIsDefaultCategory();
                this.displayHeader = this.categoryHasSubCat(this.selectedCategory);
                if (!this.isDefaultCategory) {
                    this.getSubNavTeasersLength();
                }
                this.parentCategory = this.casinoLobbyService.getCategoryInfo(this.lobbyType, this.selectedCategory?.parentCategoryId);
                this.title = this.selectedCategory.categoryname.toString();
                this.categoryname = this.selectedCategory.categoryname.toString();
                this.displayTitle = this.selectedCategory.categoryname.toString();
                this.rxEffects.register(this.jackpotAndPromotionsService.jackpotGroupObservable, (jackpotGroupId: string) => {
                    const jackpotsConfig = this.configProviderService.provideJackpotConfig();
                    this.snapShotGroupId = jackpotGroupId;
                    this.jackpotTileCategory = jackpotsConfig.enableJackpotTile && this.snapShotGroupId;
                    if (this.jackpotTileCategory) {
                        this.rxEffects.register(this.jackpotDataService.jackpotTilesFeedObservable, (res) => {
                            if (res) {
                                let response: any = Object.values(res);
                                if (this.snapShotGroupId && !this.showV2JackpotModule) {
                                    response = response.filter((x: any) => x.jackpotGroupId == this.snapShotGroupId);
                                    this.title = response[0]?.jackpotGroupName;
                                    this.jackpotGroupType = response[0]?.jackpotType;
                                    this.jackpotGroupName = response[0]?.jackpotGroupName;
                                    this.displayTitle = response[0]?.jackpotGroupName;
                                }
                            }
                        });
                    } else {
                        this.selectedCategory.categoryname = this.categoryname.toString();
                        this.title = this.categoryname.toString();
                        this.displayTitle = this.categoryname.toString();
                    }
                });
                this.isFullLobbyLoaded = true;
                this.prepareSubNavigation();
            }
        }
    }

    private adjustSubNavOnLandscapeMode() {
        if (window.matchMedia('(orientation:landscape)').matches && this.deviceService.windowWidth() < 480) {
            document.getElementsByTagName('html')[0].classList.add('add-sticky-navigation');
            if (document.querySelector(this.stickyInputQueryElement)) {
                document.querySelector(this.stickyInputQueryElement)?.setAttribute('class', 'sb-top-unset-cls');
            }
            if (document.querySelector(this.stickyInputQueryElement + '.sb-fixed-style')) {
                document.querySelector(this.stickyInputQueryElement)?.classList.remove('sb-fixed-style');
            }
            if (document.querySelector('.casino-lobby-games')) {
                document.querySelector('.casino-lobby-games')?.setAttribute('style', 'margin-top:0');
            }
        } else {
            if (this.hasHeaderElement()) {
                document.getElementsByTagName('html')[0].classList.remove('add-sticky-navigation');
                if (document.querySelector(this.stickyInputQueryElement)) {
                    document.querySelector(this.stickyInputQueryElement)?.classList.remove('sb-top-unset-cls');
                    document.querySelector(this.stickyInputQueryElement)?.setAttribute('style', `top: ${this.headerElementHeight()}px;`);
                }
                this.adjustSubNav();
            }
        }
    }

    getFromCookie(key: any): boolean {
        if (this.cookie.get(key) > '0') {
            return true;
        }
        return false;
    }

    hideSubNavigationMenu(hideSubNavMenu: boolean) {
        this.showLastLevelCategoryDropdown = !hideSubNavMenu;
    }

    subNavigationMenuInteraction(event: any) {
        this.showLastLevelCategoryDropdown = !this.showLastLevelCategoryDropdown;
        event.stopPropagation();
    }

    prepareSubNavigation() {
        const casinoConfig: any = this.configProviderService.provideCasinoConfig();
        if (this.selectedCategory && casinoConfig.maxNavigationLevel > 1) {
            let linkedToCategories: any[] = [];
            const isCurrentAGhost: boolean = !this.selectedCategory.isNavigationCategory;
            if (isCurrentAGhost) {
                linkedToCategories = this.casinoLobbyService.getLinkedToCategory(this.lobbyType, this.selectedCategory.lmtCategoryId);
            }
            const parenttoLinkedcategories: any[] = [];
            if (linkedToCategories.length > 0) {
                for (let i = 0; i < linkedToCategories.length; i++) {
                    if (linkedToCategories[i] && linkedToCategories[i].parentCategoryId) {
                        parenttoLinkedcategories.push(
                            this.casinoLobbyService?.getCategoryInfo(this.lobbyType, linkedToCategories[i].parentCategoryId).categoryInfo,
                        );
                    }
                }
            } else {
                if (this.selectedCategory?.parentCategoryId) {
                    parenttoLinkedcategories.push(
                        this.casinoLobbyService.getCategoryInfo(this.lobbyType, this.selectedCategory.parentCategoryId).categoryInfo,
                    );
                }
            }
            this.prevLobbyCategories = [];
            for (let i = 0; i < parenttoLinkedcategories.length; i++) {
                const isDefault = this.casinoLobbyService.isDefaultCategory(this.lobbyType, parenttoLinkedcategories[i].route);
                if (parenttoLinkedcategories[i] && !isDefault) {
                    let linkedSubCategories: any = [];
                    linkedSubCategories = parenttoLinkedcategories[i].lmtSubCategories;
                    if (linkedSubCategories && linkedSubCategories.length > 0) {
                        for (let j = 0; j < linkedSubCategories.length; j++) {
                            let linkedSubCategoryInfo: any;
                            linkedSubCategoryInfo = this.casinoLobbyService.getCategoryInfo(this.lobbyType, linkedSubCategories[j]).categoryInfo;
                            if (linkedSubCategoryInfo.linkedCategory) {
                                const linkedCategoryInfo = this.casinoLobbyService.getCategoryInfoOnLMTId(
                                    this.lobbyType,
                                    linkedSubCategoryInfo.linkedCategory,
                                );
                                linkedSubCategoryInfo.dropdownhref = linkedCategoryInfo?.categoryid;
                            } else {
                                linkedSubCategoryInfo.dropdownhref = linkedSubCategoryInfo?.categoryid;
                            }
                            const prevExistingCategories: any = this.prevLobbyCategories.filter((plc: any) => {
                                return linkedSubCategoryInfo.dropdownhref === plc.dropdownhref;
                            });
                            if (!(prevExistingCategories && prevExistingCategories.length > 0)) {
                                this.prevLobbyCategories.push(linkedSubCategoryInfo);
                            }
                        }
                    }
                }
            }
        }
    }

    categoryHasSubCat(category: any) {
        if (this.enableHeaderforCategory || (this.isMobileWidth() && !this.enableFixedMobileNavigation)) {
            return true;
        }
        return category.lmtSubCategories?.length;
    }

    categoryTracking(categoryType: any) {
        const previousUrl = window.location.href;
        const array = previousUrl.split('/');
        let lastsegment = array[array.length - 1];
        if (lastsegment == 'casino' || this.isTouch()) {
            lastsegment = this.casinoManager.getDefaultCategoryType();
        }
        this.tracking.updateDataLayer({ 'page.name': categoryType, 'eventName': 'pageView', 'c15': '?source=' + lastsegment });
    }
    goBack() {
        document.getElementsByTagName('html')[0].classList.remove('casino-sub-nav-thumbnail');
        if (this.jackpotTileCategory) {
            this.jackpotAndPromotionsService.jackpotGroupSubject.next('');
            this.navigation.goTo(this.navigation.location.pathname);
            this.tracking.triggerEvent('Event.Tracking', {
                'component.EventDetails': this.jackpotGroupName,
                'component.PositionEvent': this.jackpotGroupType,
                'component.LocationEvent': this.jackpotGroupName,
                'component.CategoryEvent': 'global jackpots',
                'component.LabelEvent': 'jackpot slots',
                'component.ActionEvent': 'close',
                'component.URLClicked': this.navigation.location.pathname,
            });
        } else if (
            this.selectedCategory &&
            this.selectedCategory.parentCategoryId &&
            this.defaultCategory &&
            this.defaultCategory.categoryid &&
            this.selectedCategory.parentCategoryId?.toLowerCase() !== this.defaultCategory?.categoryid?.toLowerCase()
        ) {
            let url = `/${this.mcPage?.lang}/${this.casinoManager?.getLobbyUrlPrefix(this.lobbyType)}/c/${this.selectedCategory?.parentCategoryId}`;
            // In this check if single domain is active and lobbyulr exist and if current lobby is sublobby
            if (this.productService.isSingleDomainApp && this.isCategoryUnderSubLobby(this.casinoManager?.getLobbyUrlPrefix(this.lobbyType))) {
                url = `/${this.mcPage?.lang}/${this.getParentLobby()}/${this.casinoManager?.getLobbyUrlPrefix(this.lobbyType)}/c/${this.selectedCategory?.parentCategoryId}`;
            }
            this.navigation.goTo(url);
        } else {
            let productForGivenLobbyType = this.casinoManager.getProductForGivenLobbyType(this.lobbyType);
            if (productForGivenLobbyType) {
                productForGivenLobbyType = productForGivenLobbyType?.toLowerCase();
            }
            if (this.enableDefaultHomeUrl && productForGivenLobbyType == this.defaultProductHomeUrl?.toLowerCase()) {
                this.router.navigate(['/']);
                const lmtData = this.configProviderService.provideLmtConfig();
                if (lmtData.appendCultureToDefaultUrl) {
                    this.navigation.goTo('/' + this.mcPage?.lang);
                }
            } else {
                const lobbyUrlPrefix = this.casinoManager.getLobbyUrlPrefix(this.lobbyType);
                let goBackUrl = '/' + this.mcPage?.lang + (lobbyUrlPrefix ? '/' + lobbyUrlPrefix : '');
                // In this check if single domain is active and lobbyulr exist and if current lobby is sublobby
                if (this.productService.isSingleDomainApp && lobbyUrlPrefix && this.isCategoryUnderSubLobby(lobbyUrlPrefix)) {
                    goBackUrl = `/${this.mcPage?.lang}/${this.getParentLobby()}/${lobbyUrlPrefix}`;
                }
                this.navigation.goTo(goBackUrl);
            }
        }
    }

    ngOnDestroy() {
        if (this.lobbyObs) {
            this.lobbyObs.unsubscribe();
        }
        if (this.categoryLookUpObs) {
            this.categoryLookUpObs.unsubscribe();
        }
        if (this.teaserObs) {
            this.teaserObs.unsubscribe();
        }
        this.hideSRTitleSubscription?.unsubscribe();
    }
    adjustSubNav() {
        if (this.hasHeaderElement()) {
            const hdrNavEleHeight = this.headerElementHeight();
            if (this.smartBannerService.hasSmartBanner()) {
                if (!this.isDefaultCategory) {
                    if (this.deviceService.windowWidth() <= this.MOBILE_BREAKPOINT && !this.hideHeaderOnScroll()) {
                        document.querySelector(this.stickyInputQueryElement)?.setAttribute('class', 'sb-fixed-style');
                        document.querySelector(this.stickyInputQueryElement)?.setAttribute('style', `top: ${hdrNavEleHeight + 78}px;`);
                    }
                } else {
                    if (document.querySelector('.casino-lobby-games')) {
                        document.querySelector('.casino-lobby-games')?.setAttribute('style', 'margin-top:0');
                    }

                    document.querySelector(this.stickyInputQueryElement)?.classList.remove('sb-fixed-style');
                }
            }
        }
    }

    setIsDefaultCategory() {
        this.isDefaultCategory = this.selectedCategory.isdefault == undefined ? false : this.selectedCategory.isdefault;
        this.defaultCategory = this.casinoLobbyService.getDefaultOrFirstCategoryInfo(this.lobbyType).categoryInfo;
        if (!this.isDefaultCategory) {
            document.getElementsByTagName('html')[0].classList.add('casino-sub-nav-thumbnail');
        }
    }

    getSubNavTeasersLength() {
        if (this.configProviderService.provideOptimizedTeaserConfig().teaserOptimization) {
            this.setOptimizedTeasersSubCategoryLength(this.selectedCategory);
        } else {
            this.setNormalTeasersSubCategoryLength(this.selectedCategory);
        }
    }
    setNormalTeasersSubCategoryLength(category: any) {
        this.normalTeasersArray = [];
        const headerTeasers = this.teasersStore.normalTeasers().teasers;
        if (
            category &&
            category.sitecoreData.showTeasers &&
            category.sitecoreData.teasers &&
            category.sitecoreData.teasers.length > 0 &&
            headerTeasers &&
            headerTeasers.length > 0
        ) {
            headerTeasers.forEach((headerTeaser: any) => {
                const index = category.sitecoreData.teasers.indexOf(
                    category.sitecoreData.teasers.filter((categoryTeaser: any) => categoryTeaser.itemName === headerTeaser.teaserId)[0],
                );
                if (index > -1 && !headerTeaser.isEmbeddedTeaser && !this.isDefaultCategory) {
                    this.normalTeasersArray.push(headerTeaser);
                }
            });
        }
        if (this.normalTeasersArray.length && this.normalTeasersArray.length > 0) {
            this.subnavigationTeaserLength = this.normalTeasersArray.length;
            this.displayCategoryHeader();
        }
    }
    setOptimizedTeasersSubCategoryLength(category: any) {
        const optimizedTeasers: any = this.optimizedTeaserManager.setOptimizedTeasersForCategory(category);
        if (optimizedTeasers) {
            if (optimizedTeasers.optimizedSubNavigationTeasers.length > 0) {
                this.optimizedTeasersArray = optimizedTeasers.optimizedSubNavigationTeasers;
                if (this.optimizedTeasersArray && this.optimizedTeasersArray.length > 0) {
                    this.subnavigationTeaserLength = this.optimizedTeasersArray.length;
                    this.displayCategoryHeader();
                }
            }
        }
    }
    displayCategoryHeader() {
        if (!this.isMobileWidth() && this.selectedCategory) {
            if (this.isSubNavTeasersPresent() || this.selectedCategory.gamelist.length === 0) {
                return (this.displayHeader = false);
            }
        }
        return false;
    }
    isSubNavTeasersPresent(): boolean {
        return this.subnavigationTeaserLength > 0;
    }
    signPostingCountHandler(signPostingCount: any) {
        this.isSignPostingCount = signPostingCount && signPostingCount > 0 ? true : false;
    }

    subscribeToCoinBalance() {
        this.coinBalSubscription = this.casinoLobbyService.coinBalanceObservable
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((coinBalance: number) => {
                if (coinBalance) {
                    this.showCoinExpiryText = true;
                    this.arcadeLobbyMessages.CoinExpiryText2 = this.arcadeLobbyMessages?.CoinExpiryText2?.replace('{{0}}', coinBalance);
                    this.arcadeLobbyMessages.coinExpiryToday = this.arcadeLobbyMessages?.coinExpiryToday?.replace('{{0}}', coinBalance);
                }
            });
    }

    hideSLotRacCategoryTitle() {
        this.hideSRTitleSubscription = this.casinoLobbyService.hideSlotRacesTitleObs
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((title: boolean) => {
                if (title) {
                    this.hideCategoryTitleForTournaments = true;
                } else {
                    this.hideCategoryTitleForTournaments = false;
                }
            });
    }

    private isCategoryUnderSubLobby(product: string): boolean {
        const lmtConfig: LMTDataClientConfig = this.configProviderService.lmtConfig;
        const mainLobby = Object.keys(lmtConfig.subLobbyType)[0];
        if (lmtConfig.subLobbyType[mainLobby].includes(product)) return true;
        return false;
    }

    private getParentLobby(): string {
        const lmtConfig: LMTDataClientConfig = this.configProviderService.lmtConfig;
        return Object.keys(lmtConfig.subLobbyType)[0];
    }
    get showNavItem() {
        return (
            (this.displayHeader && (this.isMobileWidth() || this.selectedCategory?.gamelist?.length > 0)) ||
            this.jackpotTileCategory ||
            (!this.isMobileWidth() && this.selectedCategory?.categoryname && this.selectedCategory?.gamesModel?.length > 0)
        );
    }
}
